import type {
  TerraApiRoute,
  TerraApiRouteResponses,
  TerraApiSuccessBody,
} from '@terra/shared-utils';
import { getCurrentGatewayUrl } from '../../utils/environmentDetection';
import { getAuthHeaders } from '../../utils/authHeaders';

export interface TenantAwareApiOptions {
  skipTenantHeader?: boolean;
}

export const getBaseUrlOverride = (serviceName: string): string => {
  const gatewayUrl = getCurrentGatewayUrl();
  return `${gatewayUrl}/${serviceName}`;
};

export const withServiceClient = (
  headers: Record<string, string>,
  serviceName: string,
): {
  overrideClientOptions: { baseUrl: string };
  extraHeaders: Record<string, string>;
} => ({
  overrideClientOptions: { baseUrl: getBaseUrlOverride(serviceName) },
  extraHeaders: headers,
});

export const withOrchestratorClient = (headers: Record<string, string>) =>
  withServiceClient(headers, 'orchestrator');

export async function callWithOrchestratorHeaders<T>({
  call,
}: {
  call: (headers: Record<string, string>) => Promise<T>;
}): Promise<T> {
  const headers = await createApiHeaders();
  return call(headers);
}

export async function tenantAwareApiWrapperOrNull<
  TRoute extends TerraApiRoute,
>({
  endpointFn,
  errorHandler,
  useBodyMessage = false,
  onFailureMessage,
}: {
  endpointFn: (
    headers: Record<string, string>,
  ) => Promise<TerraApiRouteResponses<TRoute>>;
  errorHandler?: (response: TerraApiRouteResponses<TRoute>) => void;
  useBodyMessage?: boolean;
  onFailureMessage: string;
}): Promise<SuccessResponseBody<TRoute> | null> {
  try {
    return await tenantAwareApiWrapper({
      endpointFn,
      errorHandler,
      useBodyMessage,
    });
  } catch (error: unknown) {
    console.warn(onFailureMessage, {
      error: error instanceof Error ? error : new Error(String(error)),
    });
    return null;
  }
}

export const createApiHeaders = async (): Promise<Record<string, string>> => {
  const authHeaders = await getAuthHeaders();
  return { ...authHeaders };
};

type SuccessResponseBody<TRoute extends TerraApiRoute> =
  TerraApiSuccessBody<TRoute>;
type SuccessStatusCode = 200 | 201 | 202 | 204;
type SuccessResponse<TRoute extends TerraApiRoute> = Extract<
  TerraApiRouteResponses<TRoute>,
  { status: SuccessStatusCode }
>;

const isSuccessStatusCode = (status: number): status is SuccessStatusCode =>
  status === 200 || status === 201 || status === 202 || status === 204;

const isSuccessResponse = <TRoute extends TerraApiRoute>(
  response: TerraApiRouteResponses<TRoute>,
): response is SuccessResponse<TRoute> => isSuccessStatusCode(response.status);

export async function tenantAwareApiWrapper<TRoute extends TerraApiRoute>({
  endpointFn,
  errorHandler,
  useBodyMessage = false,
}: {
  endpointFn: (
    headers: Record<string, string>,
  ) => Promise<TerraApiRouteResponses<TRoute>>;
  errorHandler?: (response: TerraApiRouteResponses<TRoute>) => void;
  useBodyMessage?: boolean;
}): Promise<SuccessResponseBody<TRoute>> {
  try {
    const headers = await createApiHeaders();
    const response = await endpointFn(headers);

    if (isSuccessResponse<TRoute>(response)) {
      return response.body;
    }

    const bodyMessage = useBodyMessage
      ? (response.body as { message?: string })?.message
      : undefined;
    const errorMessage =
      bodyMessage ?? `API call failed with status: ${response.status}`;
    console.warn(errorMessage);
    errorHandler?.(response);
    throw new Error(errorMessage);
  } catch (error) {
    console.error('API call error:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Unknown error');
  }
}
